import {equals, isNil} from 'ramda';

import {omneticApi} from '@dms/api/base';

import {saleVehicleApi} from '../saleVehicle/api';
import {
  AddFilesToSaleVehicleAlbumApiArgSaleVehiclePhoto,
  AddFilesToSaleVehicleAlbumApiResponseSaleVehiclePhoto,
  AddToSaleVehicleApiArgSaleVehiclePhoto,
  AddToSaleVehicleApiResponseSaleVehiclePhoto,
  CopySaleVehiclePhotoApiArgSaleVehiclePhoto,
  CopySaleVehiclePhotoApiResponseSaleVehiclePhoto,
  CreateSaleVehicleAlbumApiArgSaleVehiclePhoto,
  CreateSaleVehicleAlbumApiResponseSaleVehiclePhoto,
  DeleteSaleVehicleAlbumApiArgSaleVehiclePhoto,
  DeleteSaleVehicleAlbumApiResponseSaleVehiclePhoto,
  DeleteSaleVehiclePhotoApiArgSaleVehiclePhoto,
  DeleteSaleVehiclePhotoApiResponseSaleVehiclePhoto,
  DownloadSaleVehicleGalleryApiArgSaleVehiclePhoto,
  DownloadSaleVehicleGalleryApiResponseSaleVehiclePhoto,
  DownloadSaleVehicleGalleryV2ApiArgSaleVehiclePhoto,
  DownloadSaleVehicleGalleryV2ApiResponseSaleVehiclePhoto,
  GetForSaleVehicleApiArgSaleVehiclePhoto,
  GetForSaleVehicleApiResponseSaleVehiclePhoto,
  GetSaleVehicleFinancingStatusApiArgSaleVehiclePhoto,
  GetSaleVehicleFinancingStatusApiResponseSaleVehiclePhoto,
  GetSaleVehiclePreviewApiArgSaleVehiclePhoto,
  GetSaleVehiclePreviewApiResponseSaleVehiclePhoto,
  GetSaleVehicleAlbumPhotoListApiArgSaleVehiclePhoto,
  GetSaleVehicleAlbumPhotoListApiResponseSaleVehiclePhoto,
  ListSaleVehicleAlbumsApiArgSaleVehiclePhoto,
  ListSaleVehicleAlbumsApiResponseSaleVehiclePhoto,
  ListSaleVehicleAlbumsByVehicleApiArgSaleVehiclePhoto,
  ListSaleVehicleAlbumsByVehicleApiResponseSaleVehiclePhoto,
  RemoveInSaleVehicleApiArgSaleVehiclePhoto,
  RemoveInSaleVehicleApiResponseSaleVehiclePhoto,
  RemoveSaleVehiclePhotosBackgroundApiArgSaleVehiclePhoto,
  RemoveSaleVehiclePhotosBackgroundApiResponseSaleVehiclePhoto,
  RotateSaleVehiclePhotosApiArgSaleVehiclePhoto,
  RotateSaleVehiclePhotosApiResponseSaleVehiclePhoto,
  SetSaleVehicleAlbumCoverPhotoApiArgSaleVehiclePhoto,
  SetSaleVehicleAlbumCoverPhotoApiResponseSaleVehiclePhoto,
  UnsetSaleVehicleAlbumCoverPhotoApiArgSaleVehiclePhoto,
  UnsetSaleVehicleAlbumCoverPhotoApiResponseSaleVehiclePhoto,
  UpdateSaleVehiclePhotoAlbumApiArgSaleVehiclePhoto,
  UpdateSaleVehiclePhotoAlbumApiResponseSaleVehiclePhoto,
  GetSaleVehicleCoverPhotoApiResponseSaleVehiclePhoto,
  GetSaleVehicleCoverPhotoApiArgSaleVehiclePhoto,
  UpdatePositionsInSaleVehicleApiResponseSaleVehiclePhoto,
  UpdatePositionsInSaleVehicleApiArgSaleVehiclePhoto,
  ResetPositionsInSaleVehicleApiResponseSaleVehiclePhoto,
  ResetPositionsInSaleVehicleApiArgSaleVehiclePhoto,
  GetSaleVehiclePhotoListApiResponseSaleVehiclePhoto,
  GetSaleVehiclePhotoListApiArgSaleVehiclePhoto,
  CopyAuditPhotoToSaleVehicleAlbumApiResponseSaleVehiclePhoto,
  CopyAuditPhotoToSaleVehicleAlbumApiArgSaleVehiclePhoto,
  ListSaleVehiclePhotosByVehicleApiResponseSaleVehiclePhoto,
  ListSaleVehiclePhotosByVehicleApiArgSaleVehiclePhoto,
  GetSaleVehicleAlbumPhotoCheckerReportApiArgSaleVehiclePhoto,
  GetSaleVehicleAlbumPhotoCheckerReportApiResponseSaleVehiclePhoto,
  CreateSaleVehicleAlbumPhotoCheckerReportApiArgSaleVehiclePhoto,
  CreateSaleVehicleAlbumPhotoCheckerReportApiResponseSaleVehiclePhoto,
} from './types';

const getReadyFinancingFileIds = (
  status: GetSaleVehicleFinancingStatusApiResponseSaleVehiclePhoto
) =>
  status
    .filter((entry) => entry.status === 'READY')
    .map((entry) => entry.fileId)
    .sort();

export const saleVehiclePhotoApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    listSaleVehicleAlbums: build.query<
      ListSaleVehicleAlbumsApiResponseSaleVehiclePhoto,
      ListSaleVehicleAlbumsApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/sale-vehicle-album`,
        params: {saleVehicleId: queryArg.saleVehicleId},
      }),
    }),
    createSaleVehicleAlbum: build.mutation<
      CreateSaleVehicleAlbumApiResponseSaleVehiclePhoto,
      CreateSaleVehicleAlbumApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/sale-vehicle-album`,
        method: 'POST',
        body: queryArg.createSaleVehicleAlbumRequestBody,
      }),
      invalidatesTags: ['SaleVehicleAlbums'],
    }),
    getSaleVehicleAlbumPhotoList: build.query<
      GetSaleVehicleAlbumPhotoListApiResponseSaleVehiclePhoto,
      GetSaleVehicleAlbumPhotoListApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/${queryArg.saleVehicleId}/sale-vehicle-album/${queryArg.saleVehicleAlbumId}/sale-vehicle-photo`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'SaleVehicleAlbumPhotos', id: queryArg.saleVehicleAlbumId},
      ],
    }),
    deleteSaleVehicleAlbum: build.mutation<
      DeleteSaleVehicleAlbumApiResponseSaleVehiclePhoto,
      DeleteSaleVehicleAlbumApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/sale-vehicle-album/${queryArg.saleVehicleAlbumId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SaleVehicleAlbums'],
    }),
    addFilesToSaleVehicleAlbum: build.mutation<
      AddFilesToSaleVehicleAlbumApiResponseSaleVehiclePhoto,
      AddFilesToSaleVehicleAlbumApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/sale-vehicle-album/${queryArg.saleVehicleAlbumId}`,
        method: 'POST',
        body: queryArg.addFilesToSaleVehicleAlbumRequestBody,
      }),
      invalidatesTags: [
        'SaleVehicleAlbumPhotos',
        'SaleVehicleAlbums',
        'SaleVehicleFlags',
        'Vehicle',
      ],
    }),
    addFilesToSaleVehicleAlbumOptimistic: build.mutation<
      boolean,
      {
        saleVehicleId: string;
        saleVehicleAlbumId: string;
        data: {fileId: string; fileUri: string};
      }
    >({
      queryFn: () => ({
        data: true,
      }),
      onQueryStarted: (queryArg, {dispatch}) => {
        dispatch(
          saleVehiclePhotoApi.util.updateQueryData(
            'getSaleVehicleAlbumPhotoList',
            {
              saleVehicleId: queryArg.saleVehicleId,
              saleVehicleAlbumId: queryArg.saleVehicleAlbumId,
            },
            (draft) => {
              const data = queryArg.data;
              const weight = draft.at(-1)?.weight ?? 0;
              draft.push({
                id: data.fileId,
                url: data.fileUri,
                remoteFileId: data.fileId,
                weight: weight + 100,
                fileId: data.fileId,
                isCoverPhoto: false,
                latestFileOperation: null,
                category: null,
              });
            }
          )
        );
      },
    }),
    copySaleVehiclePhoto: build.mutation<
      CopySaleVehiclePhotoApiResponseSaleVehiclePhoto,
      CopySaleVehiclePhotoApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/sale-vehicle-photo/copy/${queryArg.saleVehicleId}`,
        method: 'PUT',
        body: queryArg.saleVehiclePhotoCopyRequestBody,
      }),
      invalidatesTags: (_, _1, queryArg) => [
        {
          type: 'SaleVehicleAlbumPhotos',
          id: queryArg.saleVehiclePhotoCopyRequestBody.sourceAlbum,
        },
        {
          type: 'SaleVehicleAlbumPhotos',
          id: queryArg.saleVehiclePhotoCopyRequestBody.destinationAlbum,
        },
        'SaleVehicleAlbums',
      ],
    }),
    rotateSaleVehiclePhotos: build.mutation<
      RotateSaleVehiclePhotosApiResponseSaleVehiclePhoto,
      RotateSaleVehiclePhotosApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/${queryArg.saleVehicleId}/album/${queryArg.saleVehicleAlbumId}/rotate-photo`,
        method: 'POST',
        body: queryArg.rotateSaleVehiclePhotosRequestBody,
      }),
      invalidatesTags: (_, _1, queryArg) => [
        {type: 'SaleVehicleAlbumPhotos', id: queryArg.saleVehicleAlbumId},
        'SaleVehicleAlbums',
      ],
    }),
    setSaleVehicleAlbumCoverPhoto: build.mutation<
      SetSaleVehicleAlbumCoverPhotoApiResponseSaleVehiclePhoto,
      SetSaleVehicleAlbumCoverPhotoApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/sale-vehicle-album/${queryArg.saleVehicleAlbumId}/cover-photo/${queryArg.saleVehiclePhotoId}`,
        method: 'PUT',
      }),
      invalidatesTags: (_, _1) => [
        {type: 'SaleVehicleAlbumPhotos'},
        {type: 'VehiclePhotos'},
        {type: 'SaleVehicleCoverPhoto'},
        {type: 'SaleVehicle'},
        {type: 'Vehicle'},
      ],
    }),
    unsetSaleVehicleAlbumCoverPhoto: build.mutation<
      UnsetSaleVehicleAlbumCoverPhotoApiResponseSaleVehiclePhoto,
      UnsetSaleVehicleAlbumCoverPhotoApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/sale-vehicle-album/${queryArg.saleVehicleAlbumId}/cover-photo`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, _1, queryArg) => [
        {type: 'SaleVehicleAlbumPhotos', id: queryArg.saleVehicleAlbumId},
      ],
    }),
    listSaleVehicleAlbumsByVehicle: build.query<
      ListSaleVehicleAlbumsByVehicleApiResponseSaleVehiclePhoto,
      ListSaleVehicleAlbumsByVehicleApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/sale-vehicle-album/by-vehicle`,
        params: {vehicleId: queryArg.vehicleId},
      }),
      providesTags: ['SaleVehicleAlbums'],
    }),
    deleteSaleVehiclePhoto: build.mutation<
      DeleteSaleVehiclePhotoApiResponseSaleVehiclePhoto,
      DeleteSaleVehiclePhotoApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/sale-vehicle-photo/${queryArg.saleVehicleId}/${queryArg.saleVehicleAlbumId}`,
        method: 'DELETE',
        body: queryArg.saleVehiclePhotoDeleteRequestBody,
      }),
      invalidatesTags: (_, _1, queryArg) => [
        {type: 'SaleVehicleAlbumPhotos', id: queryArg.saleVehicleAlbumId},
        {type: 'SaleVehicleCoverPhoto'},
        {type: 'VehiclePhotos'},
        {type: 'Vehicle'},
        {type: 'SaleVehicleFlags'},
        {type: 'SaleVehicleAlbums'},
      ],
    }),
    updateSaleVehiclePhotoAlbum: build.mutation<
      UpdateSaleVehiclePhotoAlbumApiResponseSaleVehiclePhoto,
      UpdateSaleVehiclePhotoAlbumApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/photo`,
        method: 'PUT',
        body: queryArg.saleVehiclePhotoUpdateAlbumRequestBody,
      }),
      invalidatesTags: (_, _1, queryArg) => [
        {
          type: 'SaleVehicleAlbumPhotos',
          id: queryArg.saleVehiclePhotoUpdateAlbumRequestBody.sourceAlbum,
        },
        {
          type: 'SaleVehicleAlbumPhotos',
          id: queryArg.saleVehiclePhotoUpdateAlbumRequestBody.destinationAlbum,
        },
        {type: 'SaleVehicleFlags'},
        {type: 'Vehicle'},
        {type: 'SaleVehicleAlbums'},
      ],
    }),
    downloadSaleVehicleGallery: build.mutation<
      DownloadSaleVehicleGalleryApiResponseSaleVehiclePhoto,
      DownloadSaleVehicleGalleryApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/${queryArg.saleVehicleId}/download-gallery/vehicle`,
        method: 'POST',
        params: {albumId: queryArg.albumId, watermarkId: queryArg.watermarkId},
      }),
    }),
    downloadSaleVehicleGalleryV2: build.mutation<
      DownloadSaleVehicleGalleryV2ApiResponseSaleVehiclePhoto,
      DownloadSaleVehicleGalleryV2ApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v2/sale-vehicle/${queryArg.saleVehicleId}/download-gallery/vehicle`,
        method: 'POST',
        params: {
          albumId: queryArg.albumId,
          applyWatermarks: queryArg.applyWatermarks,
          maxBytes: queryArg.maxBytes,
        },
      }),
    }),
    removeSaleVehiclePhotosBackground: build.mutation<
      RemoveSaleVehiclePhotosBackgroundApiResponseSaleVehiclePhoto,
      RemoveSaleVehiclePhotosBackgroundApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/${queryArg.saleVehicleId}/sale-vehicle-album/${queryArg.saleVehicleAlbumId}/remove-photo-background`,
        method: 'POST',
        body: queryArg.removeSaleVehiclePhotosBackgroundRequestBody,
      }),
      invalidatesTags: (_, _1, queryArg) => [
        {
          type: 'SaleVehicleAlbumPhotos',
          id: queryArg.saleVehicleAlbumId,
        },
      ],
    }),
    addPromoPhotosToSaleVehicle: build.mutation<
      AddToSaleVehicleApiResponseSaleVehiclePhoto,
      AddToSaleVehicleApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/promotional-photo/sale-vehicle/add-all-to-sale-vehicle`,
        method: 'POST',
        body: queryArg.addToSaleVehicleRequestBody,
      }),
      invalidatesTags: ['SaleVehiclePromoPhotos', 'Vehicle', 'SaleVehicle'],
    }),
    getPromoPhotosForSaleVehicle: build.query<
      GetForSaleVehicleApiResponseSaleVehiclePhoto,
      GetForSaleVehicleApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/promotional-photo/sale-vehicle/${queryArg.saleVehicleId}`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'SaleVehiclePromoPhotos', id: queryArg.saleVehicleId},
      ],
    }),
    // Shares the promo list tag so add/remove promo mutations refetch the status too.
    getSaleVehicleFinancingStatus: build.query<
      GetSaleVehicleFinancingStatusApiResponseSaleVehiclePhoto,
      GetSaleVehicleFinancingStatusApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/promotional-photo/sale-vehicle/${queryArg.saleVehicleId}/financing-status`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'SaleVehiclePromoPhotos', id: queryArg.saleVehicleId},
      ],
      // The BE renders financing cards asynchronously. When a (polled) status shows a different set
      // of rendered files, refetch the sources the gallery builds its promo tiles from.
      onQueryStarted: async (queryArg, {dispatch, getCacheEntry, queryFulfilled}) => {
        const previousStatus = getCacheEntry().data;
        const {data: status} = await queryFulfilled.catch(() => ({data: undefined}));
        if (
          isNil(previousStatus) ||
          isNil(status) ||
          equals(getReadyFinancingFileIds(previousStatus), getReadyFinancingFileIds(status))
        ) {
          return;
        }

        dispatch(
          saleVehiclePhotoApi.endpoints.getPromoPhotosForSaleVehicle.initiate(
            {saleVehicleId: queryArg.saleVehicleId},
            {forceRefetch: true, subscribe: false}
          )
        );
        dispatch(
          saleVehicleApi.endpoints.getSaleVehicleFromSaleVehicleId.initiate(
            {vehicleId: queryArg.saleVehicleId},
            {forceRefetch: true, subscribe: false}
          )
        );
      },
    }),
    removeInSaleVehicle: build.mutation<
      RemoveInSaleVehicleApiResponseSaleVehiclePhoto,
      RemoveInSaleVehicleApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/promotional-photo/sale-vehicle/remove-in-sale-vehicle`,
        method: 'DELETE',
        body: queryArg.removeInSaleVehicleRequestBody,
      }),
      invalidatesTags: ['SaleVehiclePromoPhotos', 'SaleVehicle'],
    }),
    getSaleVehicleWatermarkPreview: build.query<
      GetSaleVehiclePreviewApiResponseSaleVehiclePhoto,
      GetSaleVehiclePreviewApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/watermark/preview/${queryArg.saleVehiclePhotoId}`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'SaleVehicleWatermarkPreview', id: queryArg.saleVehiclePhotoId},
      ],
    }),
    getSaleVehicleCoverPhoto: build.query<
      GetSaleVehicleCoverPhotoApiResponseSaleVehiclePhoto,
      GetSaleVehicleCoverPhotoApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/${queryArg.saleVehicleId}/cover-photo`,
      }),
      providesTags: ['SaleVehicleCoverPhoto'],
    }),
    updatePositionsInSaleVehicle: build.mutation<
      UpdatePositionsInSaleVehicleApiResponseSaleVehiclePhoto,
      UpdatePositionsInSaleVehicleApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/promotional-photo/sale-vehicle/positions`,
        method: 'PUT',
        body: queryArg.updatePositionsInSaleVehicleRequestBody,
      }),
      invalidatesTags: ['SaleVehiclePromoPhotos', 'SaleVehicle'],
    }),
    resetPositionsInSaleVehicle: build.mutation<
      ResetPositionsInSaleVehicleApiResponseSaleVehiclePhoto,
      ResetPositionsInSaleVehicleApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/promotional-photo/sale-vehicle/reset-positions-in-sale-vehicle`,
        method: 'POST',
        body: queryArg.resetPositionsInSaleVehicleRequestBody,
      }),
      invalidatesTags: ['SaleVehiclePromoPhotos', 'SaleVehicle'],
    }),
    getSaleVehiclePhotoList: build.query<
      GetSaleVehiclePhotoListApiResponseSaleVehiclePhoto,
      GetSaleVehiclePhotoListApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/${queryArg.saleVehicleId}/photo`,
      }),
      providesTags: ['SaleVehicleAlbumPhotos', 'SaleVehicleCoverPhoto'],
    }),
    copyAuditPhotoToSaleVehicleAlbum: build.mutation<
      CopyAuditPhotoToSaleVehicleAlbumApiResponseSaleVehiclePhoto,
      CopyAuditPhotoToSaleVehicleAlbumApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/${queryArg.saleVehicleId}/copy-audit-photo`,
        method: 'POST',
        body: queryArg.saleVehicleAuditPhotoCopyRequestBody,
      }),
      invalidatesTags: ['SaleVehicleAlbumPhotos', 'SaleVehicleAlbums', 'Vehicle'],
    }),
    listSaleVehiclePhotosByVehicle: build.query<
      ListSaleVehiclePhotosByVehicleApiResponseSaleVehiclePhoto,
      ListSaleVehiclePhotosByVehicleApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle/sale-vehicle-photo/by-vehicle`,
        params: {vehicleId: queryArg.vehicleId},
      }),
      providesTags: ['SaleVehicleAlbumPhotos', 'SaleVehicleCoverPhoto'],
    }),
    getSaleVehicleAlbumPhotoCheckerReport: build.query<
      GetSaleVehicleAlbumPhotoCheckerReportApiResponseSaleVehiclePhoto,
      GetSaleVehicleAlbumPhotoCheckerReportApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle-album/${queryArg.saleVehicleAlbumId}/photo-check-report`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'PhotoCheckerReport', id: queryArg.saleVehicleAlbumId},
      ],
    }),
    createSaleVehicleAlbumPhotoCheckerReport: build.mutation<
      CreateSaleVehicleAlbumPhotoCheckerReportApiResponseSaleVehiclePhoto,
      CreateSaleVehicleAlbumPhotoCheckerReportApiArgSaleVehiclePhoto
    >({
      query: (queryArg) => ({
        url: `/dms/v1/sale-vehicle-album/${queryArg.saleVehicleAlbumId}/photo-check-report`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, queryArg) => [
        {type: 'PhotoCheckerReport', id: queryArg.saleVehicleAlbumId},
      ],
    }),
  }),
});

export const {
  useListSaleVehicleAlbumsQuery,
  useCreateSaleVehicleAlbumMutation,
  useGetSaleVehicleAlbumPhotoListQuery,
  useAddFilesToSaleVehicleAlbumMutation,
  useAddFilesToSaleVehicleAlbumOptimisticMutation,
  useCopySaleVehiclePhotoMutation,
  useCopyAuditPhotoToSaleVehicleAlbumMutation,
  useRotateSaleVehiclePhotosMutation,
  useDeleteSaleVehicleAlbumMutation,
  useUnsetSaleVehicleAlbumCoverPhotoMutation,
  useSetSaleVehicleAlbumCoverPhotoMutation,
  useListSaleVehicleAlbumsByVehicleQuery,
  useLazyListSaleVehicleAlbumsByVehicleQuery,
  useLazyGetSaleVehicleAlbumPhotoListQuery,
  useDeleteSaleVehiclePhotoMutation,
  useUpdateSaleVehiclePhotoAlbumMutation,
  useDownloadSaleVehicleGalleryMutation,
  useDownloadSaleVehicleGalleryV2Mutation,
  useRemoveSaleVehiclePhotosBackgroundMutation,
  useAddPromoPhotosToSaleVehicleMutation,
  useGetPromoPhotosForSaleVehicleQuery,
  useGetSaleVehicleFinancingStatusQuery,
  useRemoveInSaleVehicleMutation,
  useLazyGetSaleVehicleWatermarkPreviewQuery,
  useGetSaleVehicleCoverPhotoQuery,
  useUpdatePositionsInSaleVehicleMutation,
  useResetPositionsInSaleVehicleMutation,
  useGetSaleVehiclePhotoListQuery,
  useListSaleVehiclePhotosByVehicleQuery,
  useGetSaleVehicleAlbumPhotoCheckerReportQuery,
  useCreateSaleVehicleAlbumPhotoCheckerReportMutation,
} = saleVehiclePhotoApi;
